import { useI18n } from '@ecom/i18n';
import { Badge, type BadgeTone, Card } from '@ecom/ui-kit';
import { type ReactElement, type ReactNode } from 'react';

/**
 * A KPI whose backend does not exist yet renders an honest "Bientôt" state,
 * never a fabricated number — the same UI-honesty rule already established
 * for the admin dashboard's plan/commission figures
 * (IMPLEMENTATION_MASTER_PLAN.md §7.4): "any screen whose data depends on an
 * unresolved decision renders an inline state — never fabricated numbers."
 * Applied here to Orders/Payments, which have no backend at all yet
 * (Sprints 13+ in the plan), not only to a business-model arbitration.
 *
 * Sprint UX/UI Merchant Dashboard V2 — extended with an optional icon
 * chip, semantic `tone`, and `trend` line (all four Dashboard KPIs now
 * carry real data — see DashboardPage.tsx). Every new prop is optional so
 * `AnalyticsPage.tsx`'s existing plain usage, and any still-genuine
 * "Bientôt" card, keep rendering unchanged.
 *
 * Tone-to-class pairs are a static, fully-literal lookup (`TONE_CLASSES`)
 * rather than a template string (`` `bg-${tone}Soft}` ``) — Tailwind's JIT
 * content scanner only picks up class names it can see verbatim in source,
 * so a templated string would silently produce unstyled chips in production.
 */
export type KpiTone = BadgeTone;

const TONE_CLASSES: Record<KpiTone, { readonly chipBg: string; readonly chipText: string }> = {
  accent: { chipBg: 'bg-accentSoft', chipText: 'text-accentInk' },
  good: { chipBg: 'bg-goodSoft', chipText: 'text-good' },
  warn: { chipBg: 'bg-warnSoft', chipText: 'text-warn' },
  critical: { chipBg: 'bg-criticalSoft', chipText: 'text-critical' },
  info: { chipBg: 'bg-infoSoft', chipText: 'text-info' },
  neutral: { chipBg: 'bg-surface2', chipText: 'text-inkSoft' },
};

export interface KpiTrend {
  readonly direction: 'up' | 'down' | 'flat';
  readonly label: string;
}

export interface KpiCardProps {
  readonly label: string;
  readonly value?: string;
  readonly hint?: string;
  readonly soonReason?: string;
  readonly icon?: ReactNode;
  readonly tone?: KpiTone;
  readonly trend?: KpiTrend;
}

const TREND_CLASS: Record<KpiTrend['direction'], string> = {
  up: 'text-good',
  down: 'text-critical',
  flat: 'text-inkFaint',
};

const TREND_ARROW: Record<KpiTrend['direction'], string> = {
  up: '▲',
  down: '▼',
  flat: '→',
};

export function KpiCard({
  label,
  value,
  hint,
  soonReason,
  icon,
  tone = 'neutral',
  trend,
}: KpiCardProps): ReactElement {
  const { t } = useI18n();
  const toneClasses = TONE_CLASSES[tone];

  return (
    <Card className="min-w-0 p-4">
      <div className="flex items-center gap-2.5">
        {icon !== undefined ? (
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${toneClasses.chipBg} ${toneClasses.chipText}`}
            aria-hidden="true"
          >
            {icon}
          </span>
        ) : null}
        <p className="min-w-0 break-words text-xs font-medium uppercase tracking-normal text-inkFaint sm:tracking-wide">
          {label}
        </p>
      </div>
      {value !== undefined ? (
        <>
          {/* The figure scales with the screen (about 14 px on a 320-px phone, up to 24 px) so « 2 465,499 DT » stays on
              one line in a two-column grid; a still longer amount wraps inside its card rather than past its edge. */}
          <p className="mt-1.5 font-mono text-[clamp(0.875rem,4.3vw,1.5rem)] font-bold leading-tight text-ink [overflow-wrap:anywhere]">
            {value}
          </p>
          {trend !== undefined ? (
            <p className={`mt-0.5 text-xs font-semibold ${TREND_CLASS[trend.direction]}`}>
              {TREND_ARROW[trend.direction]} {trend.label}
            </p>
          ) : hint !== undefined ? (
            <p className="mt-0.5 text-xs text-inkFaint">{hint}</p>
          ) : null}
        </>
      ) : (
        <div className="mt-1.5 flex items-center gap-2">
          <Badge tone="neutral">{t('merchant.dashboard.kpiSoonBadge')}</Badge>
          <span className="text-xs text-inkFaint">
            {soonReason ?? t('merchant.dashboard.kpiSoonDefaultReason')}
          </span>
        </div>
      )}
    </Card>
  );
}
